/**
 * Color Palette System — preset palettes, element color map, and resolver.
 *
 * PALETTE CATEGORIES (20 presets total):
 *   classic    — Magic Forest + Paper + Rosé + Earth + Slate (5 palettes)
 *   highlights — Mint, Sky, Coral, Lavender, Gold (kicker/labels colored, 5 palettes)
 *   vivid      — 10 palettes where EVERYTHING is colored including body text
 *
 * All color values sourced from constants/colors.js — never hardcoded.
 */

import { colors, hexToRgb, luminance } from './colors';


// ─── Helper: build rgba from hex + alpha ─────────────────────────────

function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}


// ─── Default Element Map ─────────────────────────────────────────────

export const DEFAULT_ELEMENTS = {
  title:         { source: 'text',   opacity: 1.0 },
  kicker:        { source: 'text',   opacity: 0.70,  group: 'muted' },
  subtitle:      { source: 'text',   opacity: 0.70,  group: 'muted' },
  body:          { source: 'text',   opacity: 0.75 },
  paragraph:     { source: 'text',   opacity: 0.70,  group: 'muted' },
  bulletText:    { source: 'text',   opacity: 0.75 },
  bulletDot:     { source: 'text',   opacity: 0.40 },
  widgetLabel:   { source: 'text',   opacity: 0.60 },
  widgetCaption: { source: 'text',   opacity: 0.80 },
  statLabel:     { source: 'text',   opacity: 0.55 },
  statValue:     { source: 'text',   opacity: 0.90 },
  calloutLabel:  { source: 'text',   opacity: 0.60 },
  calloutText:   { source: 'text',   opacity: 0.80 },
  progressLabel: { source: 'text',   opacity: 0.65 },
  progressValue: { source: 'text',   opacity: 0.50 },
  progressFill:  { source: 'accent', opacity: 0.60 },
  progressTrack: { source: 'tint',   opacity: 0.10 },
  tagText:       { source: 'text',   opacity: 0.75 },
  tagBg:         { source: 'tint',   opacity: 0.05 },
  tagBorder:     { source: 'tint',   opacity: 0.10 },
  footer:        { source: 'text',   opacity: 0.55,  group: 'subtle' },
  footerCta:     { source: 'text',   opacity: 0.65 },
};


// ─── Shared Element Override Patterns ────────────────────────────────

/** Highlights: colored kicker + labels, title and body stay default (white) */
export const HIGHLIGHT_ELEMENTS = {
  kicker:        { source: 'accentSoft', opacity: 0.90 },
  bulletDot:     { source: 'accent',     opacity: 0.65 },
  widgetLabel:   { source: 'accentSoft', opacity: 0.70 },
  calloutLabel:  { source: 'accentSoft', opacity: 0.75 },
  statValue:     { source: 'accentSoft', opacity: 0.95 },
  tagText:       { source: 'accent',     opacity: 0.90 },
  tagBg:         { source: 'accent',     opacity: 0.08 },
  tagBorder:     { source: 'accent',     opacity: 0.40 },
  progressFill:  { source: 'accent',     opacity: 0.70 },
};

/** Vivid: EVERYTHING colored — title, body, kicker, labels, bullets, footer */
export const VIVID_ELEMENTS = {
  title:         { source: 'accent',     opacity: 1.0 },
  kicker:        { source: 'accentSoft', opacity: 0.90 },
  subtitle:      { source: 'accent',     opacity: 0.55 },
  body:          { source: 'accentSoft', opacity: 0.85 },
  paragraph:     { source: 'accentSoft', opacity: 0.80 },
  bulletText:    { source: 'accentSoft', opacity: 0.85 },
  bulletDot:     { source: 'accent',     opacity: 0.70 },
  widgetLabel:   { source: 'accentSoft', opacity: 0.65 },
  widgetCaption: { source: 'accentSoft', opacity: 0.80 },
  calloutLabel:  { source: 'accent',     opacity: 0.70 },
  calloutText:   { source: 'accentSoft', opacity: 0.80 },
  statLabel:     { source: 'accentSoft', opacity: 0.60 },
  statValue:     { source: 'accent',     opacity: 0.95 },
  progressLabel: { source: 'accentSoft', opacity: 0.65 },
  progressValue: { source: 'accentSoft', opacity: 0.55 },
  progressFill:  { source: 'accent',     opacity: 0.75 },
  tagText:       { source: 'accentSoft', opacity: 0.80 },
  tagBg:         { source: 'accent',     opacity: 0.10 },
  tagBorder:     { source: 'accent',     opacity: 0.18 },
  footer:        { source: 'accentSoft', opacity: 0.55 },
  footerCta:     { source: 'accent',     opacity: 0.70 },
};


// ─── Palette Presets ─────────────────────────────────────────────────

export const PALETTE_PRESETS = [

  // ═══════════════════════════════════════════════════════════════════
  // CLASSIC — Magic Forest + Paper + Rosé + Earth  (no group label)
  // ═══════════════════════════════════════════════════════════════════

  {
    id: 'forest',
    name: 'Magic Forest',
    description: 'Universal — pure white on dark',
    family: 'white',
    isDark: true,
    category: 'classic',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.white,
      accentSoft:   colors.white,
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: {
      tagText:     { source: 'text',   opacity: 0.85 },
      tagBorder:   { source: 'text',   opacity: 0.35 },
    },
  },

  {
    id: 'paper',
    name: 'Dark Ink',
    description: 'Clean — dark text on light backgrounds',
    family: 'slate',
    isDark: false,
    category: 'classic',
    colors: {
      textPrimary:  colors.slate[800],
      textMuted:    rgba(colors.slate[800], 0.65),
      textSubtle:   rgba(colors.slate[800], 0.45),
      accent:       colors.slate[500],
      accentSoft:   colors.slate[400],
      glassTint:    colors.black,
      surface:      'rgba(255,255,255,0.50)',
    },
    elements: {},
  },

  {
    id: 'rose',
    name: 'Rosé',
    description: 'Warm — pink on white',
    family: 'rose',
    isDark: false,
    category: 'classic',
    colors: {
      textPrimary:  colors.gray[800],
      textMuted:    rgba(colors.gray[800], 0.65),
      textSubtle:   rgba(colors.gray[800], 0.45),
      accent:       colors.rose[400],
      accentSoft:   colors.pink[300],
      glassTint:    colors.black,
      surface:      'rgba(255,255,255,0.50)',
    },
    elements: {},
  },

  {
    id: 'earth',
    name: 'Earth',
    description: 'Natural — amber on white',
    family: 'amber',
    isDark: false,
    category: 'classic',
    colors: {
      textPrimary:  colors.gray[800],
      textMuted:    rgba(colors.gray[800], 0.65),
      textSubtle:   rgba(colors.gray[800], 0.45),
      accent:       colors.amber[600],
      accentSoft:   colors.amber[400],
      glassTint:    colors.black,
      surface:      'rgba(255,255,255,0.50)',
    },
    elements: {},
  },

  {
    id: 'slate',
    name: 'Slate',
    description: 'Cool — blue-gray editorial',
    family: 'slate',
    isDark: false,
    category: 'classic',
    colors: {
      textPrimary:  colors.slate[700],
      textMuted:    rgba(colors.slate[700], 0.65),
      textSubtle:   rgba(colors.slate[700], 0.45),
      accent:       colors.slate[400],
      accentSoft:   colors.blue[300],
      glassTint:    colors.black,
      surface:      'rgba(255,255,255,0.50)',
    },
    elements: {},
  },

  {
    id: 'normies',
    name: 'Normies',
    description: 'Pixel ink — dark on parchment',
    family: 'slate',
    isDark: false,
    category: 'classic',
    colors: {
      textPrimary:  '#1a1a1a',
      textMuted:    'rgba(26,26,26,0.65)',
      textSubtle:   'rgba(72,73,75,0.80)',
      accent:       '#48494b',
      accentSoft:   '#737577',
      glassTint:    '#48494b',
      surface:      'rgba(245,245,240,0.50)',
    },
    elements: {
      tagText:     { source: 'text',   opacity: 0.90 },
      tagBorder:   { source: 'text',   opacity: 0.30 },
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // HIGHLIGHTS — Colored kicker + labels, title and body stay white
  // ═══════════════════════════════════════════════════════════════════

  {
    id: 'mint',
    name: 'Mint',
    description: 'Fresh green highlights',
    family: 'emerald',
    isDark: true,
    category: 'highlights',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.emerald[400],
      accentSoft:   colors.emerald[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...HIGHLIGHT_ELEMENTS },
  },

  {
    id: 'sky',
    name: 'Sky',
    description: 'Cool blue highlights',
    family: 'sky',
    isDark: true,
    category: 'highlights',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.sky[400],
      accentSoft:   colors.sky[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...HIGHLIGHT_ELEMENTS },
  },

  {
    id: 'coral',
    name: 'Coral',
    description: 'Warm rose highlights',
    family: 'rose',
    isDark: true,
    category: 'highlights',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.rose[400],
      accentSoft:   colors.orange[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...HIGHLIGHT_ELEMENTS },
  },

  {
    id: 'lavender',
    name: 'Lavender',
    description: 'Creative violet highlights',
    family: 'violet',
    isDark: true,
    category: 'highlights',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.violet[400],
      accentSoft:   colors.violet[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...HIGHLIGHT_ELEMENTS },
  },

  {
    id: 'gold',
    name: 'Gold',
    description: 'Warm amber highlights',
    family: 'amber',
    isDark: true,
    category: 'highlights',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.amber[400],
      accentSoft:   colors.yellow[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...HIGHLIGHT_ELEMENTS },
  },

  // ═══════════════════════════════════════════════════════════════════
  // VIVID — Everything colorful including body text
  // Title uses accent (bold 400 shade), body uses accentSoft (light 300 shade)
  // ═══════════════════════════════════════════════════════════════════

  {
    id: 'neon',
    name: 'Neon',
    description: 'Electric — sky & mint',
    family: 'sky',
    isDark: true,
    category: 'vivid',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.sky[400],
      accentSoft:   colors.emerald[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...VIVID_ELEMENTS },
  },

  {
    id: 'sunrise',
    name: 'Sunrise',
    description: 'Warm glow — orange & blush',
    family: 'orange',
    isDark: true,
    category: 'vivid',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.orange[400],
      accentSoft:   colors.pink[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...VIVID_ELEMENTS },
  },

  {
    id: 'meadow',
    name: 'Meadow',
    description: 'Natural — green & sky',
    family: 'emerald',
    isDark: true,
    category: 'vivid',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.emerald[400],
      accentSoft:   colors.sky[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...VIVID_ELEMENTS },
  },

  {
    id: 'bubblegum',
    name: 'Bubblegum',
    description: 'Playful — pink & lavender',
    family: 'pink',
    isDark: true,
    category: 'vivid',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.pink[400],
      accentSoft:   colors.violet[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...VIVID_ELEMENTS },
  },

  {
    id: 'volcano',
    name: 'Volcano',
    description: 'Fiery — red & amber',
    family: 'red',
    isDark: true,
    category: 'vivid',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.red[400],
      accentSoft:   colors.amber[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...VIVID_ELEMENTS },
  },

  {
    id: 'aurora',
    name: 'Aurora',
    description: 'Dreamy — violet & mint',
    family: 'violet',
    isDark: true,
    category: 'vivid',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.violet[400],
      accentSoft:   colors.emerald[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...VIVID_ELEMENTS },
  },

  {
    id: 'prism',
    name: 'Prism',
    description: 'Deep — indigo & gold',
    family: 'indigo',
    isDark: true,
    category: 'vivid',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.indigo[400],
      accentSoft:   colors.amber[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...VIVID_ELEMENTS },
  },

  {
    id: 'lagoon',
    name: 'Lagoon',
    description: 'Tropical — teal & rose',
    family: 'teal',
    isDark: true,
    category: 'vivid',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.teal[400],
      accentSoft:   colors.rose[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...VIVID_ELEMENTS },
  },

  {
    id: 'solar',
    name: 'Solar',
    description: 'Radiant — amber & orange',
    family: 'amber',
    isDark: true,
    category: 'vivid',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.amber[400],
      accentSoft:   colors.orange[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...VIVID_ELEMENTS },
  },

  {
    id: 'citrus',
    name: 'Citrus',
    description: 'Fresh — yellow & emerald',
    family: 'yellow',
    isDark: true,
    category: 'vivid',
    colors: {
      textPrimary:  colors.white,
      textMuted:    rgba(colors.white, 0.75),
      textSubtle:   rgba(colors.white, 0.55),
      accent:       colors.yellow[400],
      accentSoft:   colors.emerald[300],
      glassTint:    colors.white,
      surface:      'rgba(0,0,0,0.45)',
    },
    elements: { ...VIVID_ELEMENTS },
  },
];


// ─── Lookup Maps ─────────────────────────────────────────────────────

const PRESET_MAP = Object.fromEntries(
  PALETTE_PRESETS.map(p => [p.id, p])
);

export const DEFAULT_PALETTE = 'forest';

export const PALETTE_CATEGORIES = [
  { id: 'classic',    label: 'Classic' },
  { id: 'highlights', label: 'Highlights' },
  { id: 'vivid',      label: 'Vivid' },
];

/** Harmony intensity levels — controls how deep the generated colors penetrate */
export const HARMONY_INTENSITIES = [
  { id: 'classic',    label: 'Classic',    description: 'White text, accent on decorations only' },
  { id: 'highlights', label: 'Highlights', description: 'Colored kickers, labels, stats' },
  { id: 'vivid',      label: 'Vivid',      description: 'Everything colored — title, body, footer' },
];

/** Returns the element override map for a given intensity level */
export function getIntensityElements(intensityId) {
  switch (intensityId) {
    case 'highlights': return { ...HIGHLIGHT_ELEMENTS };
    case 'vivid':      return { ...VIVID_ELEMENTS };
    case 'classic':
    default:           return {};
  }
}


// ─── Depth Levels (Session 41 — Palette UX Redesign) ─────────────────

/**
 * Depth levels for the new harmony grid.
 * Each row represents increasing design sophistication.
 * `anchorToBase`: true = title always shows the user's chosen base colour.
 * `elementsId`: maps to getIntensityElements() for backward compatibility.
 */
export const DEPTH_LEVELS = [
  {
    id: 'direct',
    label: 'Direct',
    short: 'Title',
    description: 'Title = your chosen colour',
    anchorToBase: true,
    elementsId: 'vivid',
  },
  {
    id: 'harmony',
    label: 'Harmony',
    short: 'Mix',
    description: 'Full harmony spread',
    anchorToBase: false,
    elementsId: 'vivid',
  },
  {
    id: 'accent',
    label: 'Accent',
    short: 'Glow',
    description: 'White text + coloured highlights',
    anchorToBase: false,
    elementsId: 'highlights',
  },
];


/**
 * Templates that have decorative accent design elements (bars, brackets, borders).
 * Causes the Accent row to appear even without stats/tags/etc.
 */
export const TEMPLATES_WITH_ACCENT_ELEMENTS = new Set([
  'sidebar-accent',
  'dynamic-diagonal',
  'corner-frame',
  'gradient-card',
  'ribbon-band',
  'clean-minimal',
]);


/**
 * Computes which depth rows are visible for the given slide + template.
 * Returns an array of DEPTH_LEVELS entries.
 *
 * Rules (from design spec):
 *   - Row 1 (Direct): ALWAYS shown.
 *   - Row 2 (Harmony): shown when slide has body, subtitle, or footer.
 *   - Row 3 (Accent): shown ONLY when accent-receiving elements exist.
 *
 * @param {Object} slide — active slide data
 * @param {string} templateId — current template id
 * @returns {Array} — visible depth levels
 */
export function computeVisibleRows(slide, templateId) {
  const visible = [DEPTH_LEVELS[0]]; // Direct is always shown

  if (!slide) return visible;

  // Row 2: Harmony — shown when slide has body, subtitle, or footer
  const hasBody = slide.bodyEnabled !== false && (slide.body || '').trim().length > 0;
  const hasSubtitle = slide.subtitleEnabled !== false && (slide.subtitle || '').trim().length > 0;
  const hasFooter = slide.footerEnabled !== false;
  const hasSecondaryText = hasBody || hasSubtitle || hasFooter;

  if (hasSecondaryText) {
    visible.push(DEPTH_LEVELS[1]); // Harmony
  }

  // Row 3: Accent — shown when accent-receiving elements exist
  const hasKicker = slide.kickerEnabled !== false && (slide.kicker || '').trim().length > 0;
  const hasBullets = slide.bulletsEnabled !== false && Array.isArray(slide.bullets) && slide.bullets.length > 0;
  const hasWidget = !!(slide.imageWidget || slide.videoWidget);
  const hasCallout = !!slide.callout;
  const hasStats = Array.isArray(slide.stats) && slide.stats.length > 0;
  const hasTags = slide.tagsEnabled !== false && Array.isArray(slide.meta) && slide.meta.length > 0;
  const hasProgress = !!slide.progress;
  const hasTemplateAccent = TEMPLATES_WITH_ACCENT_ELEMENTS.has(templateId);

  const hasAccentTargets = hasKicker || hasBullets || hasWidget || hasCallout
    || hasStats || hasTags || hasProgress || hasTemplateAccent;

  if (hasAccentTargets) {
    visible.push(DEPTH_LEVELS[2]); // Accent
  }

  return visible;
}


/**
 * Foundation presets — canonical base colours from TEXT_ACCENT_COLORS.
 * Each entry has enough info to seed the harmony grid.
 * White/Black are achromatic and bypass the harmony grid.
 */
export const FOUNDATION_PRESETS = [
  // White/Black/Gray: realSat is the ACTUAL hex saturation (for achromatic detection).
  // hue/sat/light are VIRTUAL values used to seed non-Mono harmony columns.
  // White seeds with forest-green (h152), Black with blue-slate (h215).
  // presetColumn: which harmony column the named preset belongs in.
  //   White→forest is pure white (achromatic) → monochromatic column.
  //   Black→paper uses slate (blue-tinted) → complementary (Blue) column.
  { id: 'white',  hex: '#ffffff',  label: 'White',  hue: 152, sat: 55, light: 78, realSat: 0,  presetId: 'forest', presetColumn: 'monochromatic' },
  { id: 'gray',   hex: '#9ca3af',  label: 'Gray',   hue: 220, sat: 20, light: 67, realSat: 9 },
  { id: 'black',  hex: '#000000',  label: 'Black',  hue: 215, sat: 25, light: 35, realSat: 0,  presetId: 'paper',  presetColumn: 'complementary' },
  // Chromatic presets: no realSat needed (sat IS the real saturation).
  { id: 'gold',   hex: '#fcd34d',  label: 'Gold',   hue: 45,  sat: 97, light: 65 },
  { id: 'green',  hex: '#6ee7b7',  label: 'Green',  hue: 152, sat: 68, light: 67 },
  { id: 'blue',   hex: '#7dd3fc',  label: 'Blue',   hue: 199, sat: 95, light: 74 },
  { id: 'violet', hex: '#c4b5fd',  label: 'Violet', hue: 258, sat: 95, light: 85 },
  { id: 'rose',   hex: '#fca5a5',  label: 'Rose',   hue: 0,   sat: 94, light: 82 },
];


// ─── Resolver ────────────────────────────────────────────────────────

export function resolvePalette(presetId, customColors = null) {
  if (presetId === 'custom' && customColors) {
    const forest = PRESET_MAP[DEFAULT_PALETTE].colors;
    return {
      textPrimary:  customColors.textPrimary  || forest.textPrimary,
      textMuted:    customColors.textMuted    || forest.textMuted,
      textSubtle:   customColors.textSubtle   || forest.textSubtle,
      accent:       customColors.accent       || forest.accent,
      accentSoft:   customColors.accentSoft   || forest.accentSoft,
      glassTint:    customColors.glassTint    || forest.glassTint,
      surface:      customColors.surface      || forest.surface,
      _elements:    customColors._elements    || {},
    };
  }

  const preset = PRESET_MAP[presetId] || PRESET_MAP[DEFAULT_PALETTE];
  return { ...preset.colors, _elements: preset.elements || {} };
}


// ─── Utility: Get Preset Info ────────────────────────────────────────

export function getPresetInfo(presetId) {
  return PRESET_MAP[presetId] || null;
}

export function getPresetSwatches(presetId) {
  const preset = PRESET_MAP[presetId];
  if (!preset) {
    const p = resolvePalette(presetId);
    return { textPrimary: p.textPrimary, accent: p.accent, accentSoft: p.accentSoft };
  }

  const c = preset.colors;
  const el = preset.elements || {};

  if (el.title) {
    const titleHex = el.title.source === 'accent' ? c.accent
      : el.title.source === 'accentSoft' ? c.accentSoft
      : c.textPrimary;
    return { textPrimary: c.textPrimary, accent: c.accent, accentSoft: c.accentSoft, titleHex };
  }

  if (el.kicker) {
    const kickerHex = el.kicker.source === 'accent' ? c.accent
      : el.kicker.source === 'accentSoft' ? c.accentSoft
      : c.textPrimary;
    return { textPrimary: c.textPrimary, accent: c.accent, accentSoft: c.accentSoft, kickerHex };
  }

  return { textPrimary: c.textPrimary, accent: c.accent, accentSoft: c.accentSoft };
}


// ─── Render Colors ──────────────────────────────────────────────────

export function computeRenderColors(resolved, templateContrast = null) {
  const textRgb    = hexToRgb(resolved.textPrimary);
  const accentRgb  = hexToRgb(resolved.accent);
  const asoftRgb   = hexToRgb(resolved.accentSoft);
  const tintRgb    = hexToRgb(resolved.glassTint);

  const _isDark = luminance(resolved.textPrimary) > 0.5;
  const surfRgb = _isDark ? { r: 0, g: 0, b: 0 } : { r: 255, g: 255, b: 255 };

  const _r = ({ r, g, b }, a) => `rgba(${r},${g},${b},${a})`;

  const sourceMap = {
    text:       textRgb,
    accent:     accentRgb,
    accentSoft: asoftRgb,
    tint:       tintRgb,
  };

  const presetElements = resolved._elements || {};
  const el = {};

  for (const [key, defaultDef] of Object.entries(DEFAULT_ELEMENTS)) {
    const isOverridden = key in presetElements;
    const def = isOverridden
      ? { ...defaultDef, ...presetElements[key] }
      : { ...defaultDef };

    const rgb = sourceMap[def.source] || textRgb;
    let opacity = def.opacity;

    if (!isOverridden && def.source === 'text' && templateContrast) {
      if (key in templateContrast) {
        opacity = templateContrast[key];
      } else if (def.group && def.group in templateContrast) {
        opacity = templateContrast[def.group];
      }
    }

    el[key] = `rgba(${rgb.r},${rgb.g},${rgb.b},${opacity})`;
  }

  return {
    isDark: _isDark,
    textFull: resolved.textPrimary,
    text: (a) => _r(textRgb, a),
    accentFull: resolved.accent,
    accent: (a) => _r(accentRgb, a),
    accentSoftFull: resolved.accentSoft,
    accentSoft: (a) => _r(asoftRgb, a),
    tint: (a) => _r(tintRgb, a),
    surface: resolved.surface,
    surfaceAt: (a) => _r(surfRgb, a),
    el,
  };
}


// ─── Smart Matching (Phase 5) ───────────────────────────────────────

const LIGHT_BG_THRESHOLD = 0.45;

export function estimateBgLuminance(slide) {
  if (slide.bgSolid) {
    const lum = luminance(slide.bgSolid);
    return { isLight: lum > LIGHT_BG_THRESHOLD, computable: true };
  }
  if (slide.gradient?.enabled || slide.generative?.enabled || slide.primitives3d?.enabled) {
    return { isLight: false, computable: true };
  }
  return { isLight: false, computable: false };
}

export function getPaletteSuggestion(slide, currentPaletteId) {
  const bg = estimateBgLuminance(slide);
  const currentPreset = PRESET_MAP[currentPaletteId || DEFAULT_PALETTE];
  if (!currentPreset) return null;

  const paletteIsDark = currentPreset.isDark;

  if (bg.computable && bg.isLight && paletteIsDark) {
    const suggestions = PALETTE_PRESETS
      .filter(p => !p.isDark && p.id !== currentPaletteId)
      .map(p => ({ id: p.id, name: p.name }));
    return {
      message: 'Light background — try a dark-text palette',
      palettes: suggestions,
    };
  }

  if (bg.computable && !bg.isLight && !paletteIsDark) {
    const suggestions = PALETTE_PRESETS
      .filter(p => p.isDark && p.id !== currentPaletteId)
      .slice(0, 3)
      .map(p => ({ id: p.id, name: p.name }));
    return {
      message: 'Dark background — try a light-text palette',
      palettes: suggestions,
    };
  }

  return null;
}
